import { useState, type ReactNode } from 'react';
import { Input, Text, makeStyles, tokens } from '@fluentui/react-components';
import { Checkmark16Filled, Dismiss16Filled, Eye24Regular, EyeOff24Regular } from '@fluentui/react-icons';
import { useT } from '../../i18n';
import { MIN_PASSWORD_LENGTH, passwordRules, type PasswordRules } from '../../utils/validators';

const useStyles = makeStyles({
  toggle: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
    display: 'flex', alignItems: 'center', color: tokens.colorNeutralForeground3,
  },
  list: { listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  item: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' },
  ok: { color: tokens.colorStatusSuccessForeground1 },
  bad: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorStatusDangerForeground1 },
});

export interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  /** 'new-password' para contraseñas nuevas/confirmación, 'current-password' solo en el login real. */
  autoComplete?: 'new-password' | 'current-password';
  ariaLabel?: string;
  onEnter?: () => void;
}

/** Input de contraseña con botón de mostrar/ocultar (ojo). */
export function PasswordField({
  value, onChange, placeholder, disabled, id, name, autoComplete = 'new-password', ariaLabel, onEnter,
}: PasswordFieldProps) {
  const s = useStyles();
  const t = useT();
  const [visible, setVisible] = useState(false);
  return (
    <Input
      id={id}
      name={name}
      value={value}
      onChange={(_, d) => onChange(d.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
      type={visible ? 'text' : 'password'}
      autoComplete={autoComplete}
      disabled={disabled}
      onKeyDown={e => { if (e.key === 'Enter') onEnter?.(); }}
      contentAfter={
        <button
          type="button"
          className={s.toggle}
          onClick={() => setVisible(v => !v)}
          aria-label={visible ? t.passwordField.hide : t.passwordField.show}
          aria-pressed={visible}
          disabled={disabled}
        >
          {visible ? <EyeOff24Regular /> : <Eye24Regular />}
        </button>
      }
    />
  );
}

/** Checklist en vivo de las reglas de fortaleza (✓ cumple / ✗ falta). */
export function PasswordStrengthChecklist({ value, minLength }: { value: string; minLength?: number }) {
  const s = useStyles();
  const t = useT();
  const rules = passwordRules(value, minLength);
  const items: Array<[keyof PasswordRules, string]> = [
    ['length', t.passwordField.ruleLength.replace('{n}', String(minLength ?? MIN_PASSWORD_LENGTH))],
    ['upper', t.passwordField.ruleUpper],
    ['lower', t.passwordField.ruleLower],
    ['number', t.passwordField.ruleNumber],
    ['special', t.passwordField.ruleSpecial],
  ];
  return (
    <div>
      <Text size={200}>{t.passwordField.rulesTitle}</Text>
      <ul className={s.list} aria-live="polite">
        {items.map(([key, label]) => {
          const ok = rules[key];
          return (
            <li key={key} className={`${s.item} ${ok ? s.ok : s.bad}`} data-rule={key} data-ok={ok}>
              {ok ? <Checkmark16Filled aria-hidden /> : <Dismiss16Filled aria-hidden />}
              <span>{label}</span>
              <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                {ok ? t.passwordField.rulePass : t.passwordField.ruleFail}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export interface NewPasswordFieldsProps {
  password: string;
  confirm: string;
  onPasswordChange: (v: string) => void;
  onConfirmChange: (v: string) => void;
  passwordPlaceholder?: string;
  disabled?: boolean;
  /** Etiquetas cuando el contenedor no las provee (ej. en LoginPage sin <Field>). */
  passwordLabel?: string;
  onEnter?: () => void;
  /** Largo mínimo de la empresa (por defecto 8). */
  minLength?: number;
  /** Hueco para etiquetas/Field del contenedor alrededor de cada input. */
  wrap?: (kind: 'password' | 'confirm', input: ReactNode) => ReactNode;
}

/**
 * Contraseña nueva + confirmación + checklist. La validez se calcula con
 * `isValidNewPassword` en el contenedor para habilitar el botón de envío.
 */
export function NewPasswordFields({
  password, confirm, onPasswordChange, onConfirmChange, passwordPlaceholder, disabled, passwordLabel, onEnter, wrap, minLength,
}: NewPasswordFieldsProps) {
  const s = useStyles();
  const t = useT();
  const wrapper = wrap ?? ((_: string, input: ReactNode) => input);
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <>
      {wrapper('password', (
        <PasswordField
          value={password}
          onChange={onPasswordChange}
          placeholder={passwordPlaceholder}
          ariaLabel={passwordLabel}
          name="agrovid-new-password"
          disabled={disabled}
          onEnter={onEnter}
        />
      ))}
      {(password.length > 0 || confirm.length > 0) && <PasswordStrengthChecklist value={password} minLength={minLength} />}
      {wrapper('confirm', (
        <PasswordField
          value={confirm}
          onChange={onConfirmChange}
          placeholder={t.passwordField.confirmLabel}
          ariaLabel={t.passwordField.confirmLabel}
          name="agrovid-confirm-password"
          disabled={disabled}
          onEnter={onEnter}
        />
      ))}
      {mismatch && (
        <Text role="alert" size={200} className={s.error}>{t.passwordField.mismatch}</Text>
      )}
    </>
  );
}
